
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmployees } from '../redux/employer-employeeSlice/EmployerEmployeeSlice';

import Chart from 'chart.js/auto';

const SuperAdminEmployerEmployee = () => {
  const dispatch = useDispatch();
  const { employees, status } = useSelector(state => state.employerEmployee);
  const staffChartRef = useRef(null);
  const salaryChartRef = useRef(null);
  const staffChartInstance = useRef(null);
  const salaryChartInstance = useRef(null);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchEmployees()); // Fetch all for superadmin
  }, [status, dispatch]);

  useEffect(() => {
    if (employees.length > 0 && staffChartRef.current && salaryChartRef.current) {
      // Destroy previous chart instances to avoid memory leaks
      if (staffChartInstance.current) staffChartInstance.current.destroy();
      if (salaryChartInstance.current) salaryChartInstance.current.destroy();

      // Aggregate data for charts
      const employerMap = {};
      employees.forEach(emp => {
        const employerName = emp.employer.companyName;
        if (!employerMap[employerName]) {
          employerMap[employerName] = { employeeCount: 0, totalSalary: 0 };
        }
        employerMap[employerName].employeeCount += 1;
        employerMap[employerName].totalSalary += emp.salary.history.reduce((sum, hist) => sum + (hist.netSalary || 0), 0);
      });

      const labels = Object.keys(employerMap);
      const employeeCounts = labels.map(name => employerMap[name].employeeCount);
      const totalSalaries = labels.map(name => employerMap[name].totalSalary);

      // Staff Distribution Chart
      staffChartInstance.current = new Chart(staffChartRef.current, {
        type: 'pie',
        data: {
          labels,
          datasets: [{
            label: 'Employees per Employer',
            data: employeeCounts,
            backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
            borderWidth: 1,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Employee Distribution by Employer' },
          },
        },
      });

      // Salary Payments Chart
      salaryChartInstance.current = new Chart(salaryChartRef.current, {
        type: 'pie',
        data: {
          labels,
          datasets: [{
            label: 'Total Salaries per Employer',
            data: totalSalaries,
            backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
            borderWidth: 1,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Total Salaries by Employer' },
          },
        },
      });
    }

    // Cleanup on unmount
    return () => {
      if (staffChartInstance.current) staffChartInstance.current.destroy();
      if (salaryChartInstance.current) salaryChartInstance.current.destroy();
    };
  }, [employees]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-6">
      <h2 className="text-4xl font-bold text-gray-800 mb-8 text-center bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">Superadmin Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
          <h3 className="text-xl font-semibold text-gray-700 mb-4">Employee Distribution</h3>
          <div className="h-64">
            <canvas ref={staffChartRef}></canvas>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
          <h3 className="text-xl font-semibold text-gray-700 mb-4">Salary Payments</h3>
          <div className="h-64">
            <canvas ref={salaryChartRef}></canvas>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
        <h3 className="text-2xl font-semibold text-gray-800 mb-4">Employee Salary Overview</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm md:text-base border-collapse">
            <thead className="bg-gradient-to-r from-blue-500 to-purple-500 text-white sticky top-0">
              <tr>
                <th className="py-3 px-4 text-left font-medium">Employer</th>
                <th className="py-3 px-4 text-left font-medium">Employee Name</th>
                <th className="py-3 px-4 text-left font-medium">Period</th>
                <th className="py-3 px-4 text-left font-medium">Gross Salary</th>
                <th className="py-3 px-4 text-left font-medium">Deductions</th>
                <th className="py-3 px-4 text-left font-medium">Net Salary</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(employee => (
                employee.salary.history.map((hist, i) => (
                  <tr key={`${employee._id}-${i}`} className="border-t hover:bg-gray-50 transition-colors duration-200">
                    <td className="py-3 px-4">{employee.employer?.companyName || employee.companyName || employee.employer}</td>
                    <td className="py-3 px-4">{employee.firstName} {employee.lastName}</td>
                    <td className="py-3 px-4">{hist.month} {hist.year}</td>
                    <td className="py-3 px-4">N{hist.grossSalary.toFixed(2)}</td>
                    <td className="py-3 px-4">N{Object.values(hist.deductions).reduce((a, b) => a + b, 0).toFixed(2)}</td>
                    <td className="py-3 px-4">N{hist.netSalary.toFixed(2)}</td>
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminEmployerEmployee;